import {
  creditMeter,
  creditsLabel,
  formatMicrosFine,
  grantExpiry,
  type AiActionKey,
  type AiGrantInput,
} from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { notify } from '../feedback.ts'
import { platformKeys } from './keys.ts'

/**
 * The platform console (admin.forhim.io, Phase 12f): every church, one
 * church's plan, override and usage, the plans themselves, and the log of
 * what operators did. The server refuses anyone who is not a platform
 * operator; these hooks only fetch.
 */

export function useAdminOverview() {
  const api = useApi()
  return useQuery({
    queryKey: platformKeys.adminOrgs(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.admin.organizations.get()
      if (error) throw error
      return data
    },
  })
}

export function useAdminChurch(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const key = platformKeys.adminOrg(id)
  const church = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await api.api.v1.admin.organizations({ id }).get()
      if (error) throw error
      return data
    },
  })
  const settle = (data: unknown) => {
    queryClient.setQueryData(key, data)
    void queryClient.invalidateQueries({ queryKey: platformKeys.adminOrgs() })
    void queryClient.invalidateQueries({ queryKey: platformKeys.adminLog() })
  }
  const setSubscription = useMutation({
    mutationFn: async (body: {
      plan: string
      status: 'trialing' | 'active' | 'canceled'
      trialEndsAt: string | null
      reason: string
    }) => {
      const { data, error } = await api.api.v1.admin.organizations({ id }).subscription.put(body)
      if (error) throw error
      return data
    },
    onSuccess: settle,
  })
  const setOverride = useMutation({
    mutationFn: async (body: {
      features: Record<string, boolean>
      limits: Record<string, number | null>
      expiresAt: string | null
      reason: string
    }) => {
      const { data, error } = await api.api.v1.admin.organizations({ id }).override.put(body)
      if (error) throw error
      return data
    },
    onSuccess: settle,
  })
  const clearOverride = useMutation({
    mutationFn: async (reason: string) => {
      const { data, error } = await api.api.v1.admin
        .organizations({ id })
        .override.delete(undefined, { query: { reason } })
      if (error) throw error
      return data
    },
    onSuccess: settle,
  })
  const impersonate = useMutation({
    mutationFn: async (body: { userId: string; reason: string }) => {
      const { error } = await api.api.v1.admin.impersonate.post(body)
      if (error) throw error
    },
  })
  return { church, setSubscription, setOverride, clearOverride, impersonate }
}

export function useAdminPlans() {
  const api = useApi()
  return useQuery({
    queryKey: platformKeys.adminPlans(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.admin.plans.get()
      if (error) throw error
      return data
    },
  })
}

export type AdminPlans = NonNullable<ReturnType<typeof useAdminPlans>['data']>
export type AdminPlan = AdminPlans['plans'][number]

/** What the plan builder edits for one plan. */
export interface PlanDraft {
  name: string
  description: string
  monthlyCents: number
  trialDays: number
  features: string[]
  limits: Record<string, number | null>
  public: boolean
  position: number
}

const draftOf = (plan: AdminPlan): PlanDraft => ({
  name: plan.name,
  description: plan.description,
  monthlyCents: plan.monthlyCents,
  trialDays: plan.trialDays,
  features: [...plan.features],
  limits: { ...plan.limits },
  public: plan.public,
  position: plan.position,
})

const same = (a: PlanDraft, b: PlanDraft) => JSON.stringify(a) === JSON.stringify(b)

/**
 * The plan builder: a matrix of plans (columns) by features and limits (rows).
 * Edits collect in a draft per plan; Save sends only the plans that changed,
 * each one at once applying to every church on it.
 */
export function usePlanBuilder() {
  const api = useApi()
  const queryClient = useQueryClient()
  const plans = useAdminPlans()
  const [drafts, setDrafts] = useState<Record<string, PlanDraft>>({})
  const [syncErrors, setSyncErrors] = useState<Record<string, string>>({})

  const draft = (plan: AdminPlan) => drafts[plan.id] ?? draftOf(plan)
  const edit = (plan: AdminPlan, change: (d: PlanDraft) => PlanDraft) =>
    setDrafts((all) => {
      const next = change(all[plan.id] ?? draftOf(plan))
      const { [plan.id]: _, ...rest } = all
      return same(next, draftOf(plan)) ? rest : { ...rest, [plan.id]: next }
    })
  const toggleFeature = (plan: AdminPlan, key: string) =>
    edit(plan, (d) => ({
      ...d,
      features: d.features.includes(key)
        ? d.features.filter((f) => f !== key)
        : [...d.features, key],
    }))
  const setLimit = (plan: AdminPlan, key: string, value: number | null) =>
    edit(plan, (d) => ({ ...d, limits: { ...d.limits, [key]: value } }))
  const dirty = Object.keys(drafts)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: platformKeys.adminPlans() })
    void queryClient.invalidateQueries({ queryKey: platformKeys.adminOrgs() })
    void queryClient.invalidateQueries({ queryKey: platformKeys.adminLog() })
  }

  const save = useMutation({
    mutationFn: async () => {
      const errors: Record<string, string> = {}
      for (const [id, body] of Object.entries(drafts)) {
        const { data, error } = await api.api.v1.admin.plans({ id }).patch(body)
        if (error) throw error
        if (data.syncError) errors[id] = data.syncError
      }
      return errors
    },
    onSuccess: (errors) => {
      setDrafts({})
      setSyncErrors(errors)
      refresh()
    },
  })

  const create = useMutation({
    mutationFn: async (body: { key: string; name: string; monthlyCents: number }) => {
      const position = (plans.data?.plans.length ?? 0) + 1
      const { data, error } = await api.api.v1.admin.plans.post({
        ...body,
        description: '',
        trialDays: 0,
        features: ['people'],
        limits: {},
        public: false,
        position,
      })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })

  const archive = useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await api.api.v1.admin.plans({ id }).archive.post({ archived })
      if (error) throw error
    },
    onSuccess: refresh,
  })

  const setRoles = useMutation({
    mutationFn: async (body: { defaultPlanId: string; signupPlanId: string }) => {
      const { error } = await api.api.v1.admin.plans.roles.put(body)
      if (error) throw error
    },
    onSuccess: refresh,
  })

  return {
    plans,
    draft,
    edit,
    toggleFeature,
    setLimit,
    dirty,
    discard: () => setDrafts({}),
    save,
    syncErrors,
    create,
    archive,
    setRoles,
  }
}

export function usePlatformLog() {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: platformKeys.adminLog(),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.admin.log.get({
        query: pageParam ? { before: pageParam } : {},
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
}

// ---- AI credits (docs/15, "AI credits") -------------------------------------------------------

/**
 * The console's AI page: each action's credits, whether it runs, and the
 * last 30 days — runs, credits charged, what they really cost — to price a
 * credit by. Changing an action applies at once, to every church.
 */
export function useAdminAi() {
  const api = useApi()
  const queryClient = useQueryClient()
  const overview = useQuery({
    queryKey: platformKeys.adminAi(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.admin.ai.get()
      if (error) throw error
      return data
    },
  })
  const update = useMutation({
    mutationFn: async ({
      key,
      ...body
    }: {
      key: AiActionKey
      credits: number
      enabled: boolean
      description: string
    }) => {
      const { data, error } = await api.api.v1.admin.ai.actions({ key }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (_, input) => {
      notify({ tone: 'success', title: `Saved ${creditsLabel(input.credits)} per run` })
      void queryClient.invalidateQueries({ queryKey: platformKeys.adminAi() })
      void queryClient.invalidateQueries({ queryKey: platformKeys.adminLog() })
    },
  })
  const data = overview.data
  return {
    overview,
    actions: data?.actions ?? [],
    usage: data?.usage ?? null,
    /** "$0.0123"; "—" when nothing was charged. */
    dollars: (micros: number | null) => (micros === null ? '—' : formatMicrosFine(micros)),
    update,
  }
}

export type AdminAiAction = ReturnType<typeof useAdminAi>['actions'][number]

/** A church's AI credits in the console: this month, its use, its grants; Grant credits. */
export function useAdminChurchCredits(id: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const key = platformKeys.adminChurchCredits(id)
  const credits = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await api.api.v1.admin.organizations({ id })['ai-credits'].get()
      if (error) throw error
      return data
    },
  })
  const grant = useMutation({
    mutationFn: async (input: AiGrantInput) => {
      const { data, error } = await api.api.v1.admin
        .organizations({ id })
        ['ai-credits'].grants.post({
          credits: input.credits,
          reason: input.reason,
          expiresAt: grantExpiry(input.expiresOn),
        })
      if (error) throw error
      return data
    },
    onSuccess: (data, input) => {
      queryClient.setQueryData(key, data)
      notify({ tone: 'success', title: `Granted ${creditsLabel(input.credits)}` })
      void queryClient.invalidateQueries({ queryKey: platformKeys.adminOrg(id) })
      void queryClient.invalidateQueries({ queryKey: platformKeys.adminLog() })
    },
  })
  const data = credits.data
  return {
    credits,
    balance: data?.balance ?? null,
    meter: data ? creditMeter(data.balance) : null,
    use: (data?.use ?? []).map((u) => ({ ...u, total: creditsLabel(u.credits) })),
    grants: (data?.grants ?? []).map((g) => ({
      ...g,
      total: creditsLabel(g.credits),
      /** "This month" or "Until Dec 31". */
      lasts: g.expiresAt
        ? `Until ${new Date(g.expiresAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}`
        : `${new Date(g.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })} only`,
    })),
    grant,
  }
}
